import React, { memo, useCallback, useState } from "react";
import { ReactSVG } from "react-svg";
import { classNames, useTranslate } from "helpers";
import { Dialog } from "ui/components/Dialog";
import { PictureIcon } from "ui/components/Pictures";
import { FileUploadListProps } from "./types";

const formatFileSize = (sizeKb: number): string => {
  if (sizeKb >= 1024) {
    return `${(sizeKb / 1024).toFixed(1)} MB`;
  }
  return `${sizeKb} KB`;
};

const getFileExtension = (fileName: string): string => {
  const ext = fileName.split(".").pop()?.toUpperCase();
  return ext ?? "";
};

const FileUploadList: React.FC<FileUploadListProps> = ({
  files,
  onDelete,
  activeId,
  onItemClick,
  deleteTitle,
  deleteMessage,
}) => {
  const { t } = useTranslate();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      setDeleteId(id);
    },
    []
  );

  const handleDeleteConfirm = useCallback(() => {
    if (deleteId && onDelete) {
      onDelete(deleteId);
    }
    setDeleteId(null);
  }, [deleteId, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteId(null);
  }, []);

  if (files.length === 0) return null;

  return (
    <>
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {files.map((file) => (
          <div
            key={file.id}
            onClick={() => onItemClick?.(file)}
            className={classNames(
              "flex items-center gap-3 p-2.5 border rounded-sm transition-colors",
              activeId === file.id
                ? "border-sky-400 bg-sky-50"
                : "border-slate-200 bg-white hover:bg-slate-50",
              onItemClick && "cursor-pointer"
            )}
          >
            {/* Thumbnail — PictureIcon (avatar/krug). */}
            <div className="flex-shrink-0">
              <PictureIcon
                url={file.url}
                fileName={file.originalFileName}
                tooltip={file.originalFileName}
                size="md"
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-700 truncate">
                {file.originalFileName}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{getFileExtension(file.originalFileName)}</span>
                <span>·</span>
                <span>{formatFileSize(file.fileSizeKb)}</span>
              </div>
            </div>

            {/* Active check */}
            {activeId === file.id && (
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                <ReactSVG src="/media/icons/check.svg" className="w-3.5 h-3.5 text-white [&_svg]:stroke-white" />
              </div>
            )}

            {/* Delete */}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => handleDeleteClick(e, file.id)}
                className="flex-shrink-0 p-1.5 rounded-full hover:bg-red-100 transition-colors"
              >
                <ReactSVG
                  src="/media/icons/trash.svg"
                  className="w-4 h-4 stroke-red-500"
                />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog
        open={!!deleteId}
        title={deleteTitle ?? t("admin.fileupload.dialog.title")}
        message={deleteMessage ?? t("admin.fileupload.dialog.message")}
        confirmText={t("admin.fileupload.dialog.confirmtext")}
        cancelText={t("admin.fileupload.dialog.canceltext")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default memo(FileUploadList);
